import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const RoleEditModal = ({ member, isOpen, onClose, onSave }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [permissions, setPermissions] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const roleOptions = [
    { value: 'Designer', label: 'Designer', description: 'Can create and manage design projects' },
    { value: 'Sales Rep', label: 'Sales Representative', description: 'Can communicate with clients and view projects' },
    { value: 'Manager', label: 'Manager', description: 'Can manage projects and team members' }
  ];

  const defaultPermissions = {
    Designer: {
      createProjects: true,
      viewAllProjects: false,
      manageTeam: false,
      accessVault: true,
      clientCommunication: false
    },
    'Sales Rep': {
      createProjects: false,
      viewAllProjects: true,
      manageTeam: false,
      accessVault: false,
      clientCommunication: true
    },
    Manager: {
      createProjects: true,
      viewAllProjects: true,
      manageTeam: true,
      accessVault: true,
      clientCommunication: true
    }
  };

  useEffect(() => {
    if (member) {
      setSelectedRole(member?.role);
      setPermissions(member?.permissions);
    }
  }, [member]);

  const handleRoleChange = (newRole) => {
    setSelectedRole(newRole);
    setPermissions(defaultPermissions?.[newRole] || {});
  };

  const handlePermissionChange = (permission, value) => {
    setPermissions(prev => ({
      ...prev,
      [permission]: value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onSave(member?.id, selectedRole, permissions);
      onClose();
    } catch (error) {
      console.error('Failed to update member:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md elevation-3">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-heading text-foreground">Edit Team Member</h2>
            <p className="text-sm text-muted-foreground">{member?.name}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Role Selection */}
          <Select
            label="Role"
            options={roleOptions}
            value={selectedRole}
            onChange={handleRoleChange}
            disabled={member?.role === 'Owner'}
          />

          {/* Permissions */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Permissions</h3>
            <div className="space-y-3">
              <Checkbox
                label="Create Projects"
                description="Can create new design projects"
                checked={permissions?.createProjects || false}
                onChange={(e) => handlePermissionChange('createProjects', e?.target?.checked)}
              />
              
              <Checkbox
                label="View All Projects"
                description="Can access all team projects"
                checked={permissions?.viewAllProjects || false}
                onChange={(e) => handlePermissionChange('viewAllProjects', e?.target?.checked)}
              />
              
              <Checkbox
                label="Manage Team"
                description="Can invite and manage team members"
                checked={permissions?.manageTeam || false}
                onChange={(e) => handlePermissionChange('manageTeam', e?.target?.checked)}
              />
              
              <Checkbox
                label="Access Vault"
                description="Can access the digital vault"
                checked={permissions?.accessVault || false}
                onChange={(e) => handlePermissionChange('accessVault', e?.target?.checked)}
              />
              
              <Checkbox
                label="Client Communication"
                description="Can communicate directly with clients"
                checked={permissions?.clientCommunication || false}
                onChange={(e) => handlePermissionChange('clientCommunication', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isLoading}
            disabled={member?.role === 'Owner'}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoleEditModal;